/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  Row, Col, Card, CardBody, CardHeader, CardText,
  CardFooter, Button, Input, InputGroup,
} from 'reactstrap';
import moment from 'moment';
import { get, put } from '../../helpers/api_helper';
import { getBestBuyRgmReasonDetails } from '../../helpers/bestbuy_rgm_helper';

function BestBuyReturn(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t, reference } = props;

  const [data, setData] = useState(null);
  const [concluding, setConcluding] = useState(false);
  const [signal, setSignal] = useState(0);

  const [denialReasons, setDenialReasons] = useState({});
  const [denialComments, setDenialComments] = useState({});

  const getReturns = async () => {
    try {
      const res = await get(`/api/bestbuy/returns/${reference.reference1}`);

      if (res !== '') {
        setData(res);
      } else {
        setData(null);
      }
    } catch (e) {
      toastr.error(t('Error loading Best Buy returns data'));
    }
  };

  useEffect(() => {
    getReturns();
  }, [reference, signal]);

  const receive = async (id) => {
    try {
      await put(`/api/bestbuy/return/receive/${id}`, { received_at: new Date(Date.now()) });

      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message, t('Error marking RGM received'));
    }
  };

  const issueCredit = async (id) => {
    try {
      if ((denialComments[id] ?? '') !== '') {
        toastr.warning(t('Did you mean to deny instead? (please erase comment if not)'));

        return;
      }

      setConcluding(true);
      await put(`/api/bestbuy/return/credit/${id}`);

      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message, t('Error crediting RGM'));
    } finally {
      setConcluding(false);
    }
  };

  const deny = async (id) => {
    const code = denialReasons[id] ?? '01';
    const comment = denialComments[id] ?? '';

    if (code === '01') {
      toastr.info(t('Please select a reason...', t('No reason selected')));

      return;
    }

    try {
      setConcluding(true);
      await put(`/api/bestbuy/return/deny/${id}`, { code, comment });

      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message, t('Error denying RGM'));
    } finally {
      setConcluding(false);
    }
  };

  const jsx = (
    <>
      {data !== null && (
        <Row>
          {data.map((rgm) => {
            const reason = getBestBuyRgmReasonDetails(
              rgm.rgm_type,
              rgm.rgm_nbr,
            );

            return (
              <Col lg="6" xl="5" xxl="4" key={`itm_${rgm.id}`}>
                <Card>
                  <CardHeader className="bg-transparent border-bottom">
                    {t('Best Buy RGM (return)')}
                  </CardHeader>
                  <CardBody>
                    <h6>
                      RGM No:
                      {' '}
                      <strong>{rgm.rgm_nbr}</strong>
                      <br />
                      Manifest No:
                      {' '}
                      <strong>{rgm.manifest}</strong>
                      <br />
                      <br />
                      {t('Part Number:')}
                      {' '}
                      {rgm.part_nbr}
                      <br />
                      {t('Description')}
                      {' '}
                      {rgm.desc}
                    </h6>
                    <CardText>
                      <strong>{t('Return Reason:')}</strong>
                      {' '}
                      {reason.detail ? `${reason.title} - ${reason.detail}` : reason.title}
                      <br />
                      <strong>{t('Reason Note:')}</strong>
                      {' '}
                      {rgm.np_notes || t('No additional notes')}
                    </CardText>
                    <CardText className="mt-3">
                      <strong>{t('Inbound Tracking:')}</strong>
                      {' '}
                      {rgm.tracking?.toUpperCase()}
                      {' ('}
                      {rgm.carrier}
                      )
                    </CardText>
                    <CardText>
                      <strong>{t('Value')}</strong>
                      {' '}
                      {rgm.amount}
                      {' '}
                      USD
                    </CardText>
                  </CardBody>
                  <CardFooter>
                    <div className="hstack gap-3">
                      {rgm.received_at !== null && rgm.credited_at === null
                        && rgm.denied_at === null && (
                        <>
                          <Button color="danger" disabled={concluding} className="btn btn-danger btn-label" size="sm" onClick={() => { issueCredit(rgm.id); }}>
                            <i className={`bx bx-check-double label-icon ${concluding && 'bx-spin'}`} />
                            {concluding ? t('Processing...') : t('Issue Credit')}
                          </Button>

                          <div>
                            <span className="fs-4">
                              OR
                            </span>
                          </div>

                          <div>
                            <InputGroup size="sm" style={{ width: '240px' }}>
                              <Input
                                type="select"
                                value={denialReasons[rgm.id] ?? '01'}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setDenialReasons((prev) => ({ ...prev, [rgm.id]: value }));
                                }}
                              >
                                <option value="01">{t('-- Please select --')}</option>
                                <option value="02">{t('60-02: Used part returned')}</option>
                                <option value="03">{t('60-03: Took too long to receive')}</option>
                                <option value="04">{t('60-04: Wrong part received')}</option>
                                <option value="05">{t('60-05: No part received')}</option>
                                <option value="06">{t('60-06: Damaged Part received')}</option>
                              </Input>
                              <Button color="danger" disabled={concluding} className="btn-soft-danger" size="sm" onClick={() => { deny(rgm.id); }}>
                                {t('Deny Claim')}
                              </Button>
                            </InputGroup>
                            <div className="mt-2">
                              <textarea
                                value={denialComments[rgm.id] ?? ''}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  setDenialComments((prev) => ({ ...prev, [rgm.id]: value }));
                                }}
                                rows="1"
                                placeholder={t('Comments')}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {rgm.credited_at !== null && (
                        <div className="hstack gap-2">
                          <Button color="success" disabled className="btn btn-success btn-label" size="sm">
                            <i className="bx bx-check-double label-icon" />
                            {t('Credited')}
                          </Button>

                          <div className="text-muted">
                            {`by ${rgm.credited_by} - ${moment(rgm.credited_at).fromNow()}`}
                          </div>
                        </div>
                      )}

                      {rgm.denied_at !== null && (
                        <div className="hstack gap-2">
                          <Button color="danger" disabled className="btn btn-success btn-label" size="sm">
                            <i className="mdi mdi-close-thick label-icon" />
                            {t('Denied')}
                          </Button>

                          <div className="text-muted">
                            {`by ${rgm.denied_by} - ${moment(rgm.denied_at).fromNow()}`}
                          </div>
                        </div>
                      )}

                      {rgm.received_at === null && (
                        <Button color="primary" className="btn btn-primary btn-label" size="sm" onClick={() => { receive(rgm.id); }}>
                          <i className="bx bx-check-double label-icon" />
                          {t('Mark Received')}
                        </Button>
                      )}
                    </div>
                  </CardFooter>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {data === null && (
        <div />
      )}
    </>
  );

  return jsx;
}

BestBuyReturn.defaultProps = {
  t: (w) => w,
};

BestBuyReturn.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  reference: PropTypes.object.isRequired,
};

export default connect(() => ({

}), {})(withTranslation()(BestBuyReturn));
